import type {
  ApplyTitleSuggestionsInput,
  ApplyTitleSuggestionsResponse,
  ApplyTitleSuggestionsResult,
  TitleSuggestion as DomainTitleSuggestion,
  TitleSuggestionIneligibleReason,
} from "@/core/title-suggestions/types"

import { getCsrfTokenFromDocumentCookie } from "./auth-api"

type WithWireDate<T> = T extends { joinedAt: Date }
  ? Omit<T, "joinedAt"> & { joinedAt: string }
  : T extends { joinedAt: Date | null }
    ? Omit<T, "joinedAt"> & { joinedAt: string | null }
    : T

export type TitleSuggestion = WithWireDate<DomainTitleSuggestion>
export type TitleSuggestionBatch = {
  sourcePeriod: ApplyTitleSuggestionsInput["sourcePeriod"]
  evaluatedOn: string
  rows: TitleSuggestion[]
}
export type ApplyTitleSuggestionResult = ApplyTitleSuggestionsResult
export type {
  ApplyTitleSuggestionsInput,
  ApplyTitleSuggestionsResponse,
  TitleSuggestionIneligibleReason,
}

export class TitleSuggestionsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = "TitleSuggestionsApiError"
  }
}

const readError = async (response: Response, fallback: string) => {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; message?: unknown }
      message?: unknown
    }
    const message =
      typeof body.error?.message === "string"
        ? body.error.message
        : typeof body.message === "string"
          ? body.message
          : fallback
    const code =
      typeof body.error?.code === "string" ? body.error.code : undefined
    return new TitleSuggestionsApiError(message, response.status, code)
  } catch {
    return new TitleSuggestionsApiError(fallback, response.status)
  }
}

const postHeaders = () => {
  const csrfToken = getCsrfTokenFromDocumentCookie()
  if (!csrfToken) throw new Error("Missing CSRF token")
  return { "content-type": "application/json", "x-csrf-token": csrfToken }
}

export const titleSuggestionsApi = {
  get: async (): Promise<TitleSuggestionBatch> => {
    const response = await fetch("/api/admin/title-suggestions", {
      method: "GET",
      credentials: "include",
    })
    if (!response.ok)
      throw await readError(response, "Failed to load rank title suggestions")
    return response.json() as Promise<TitleSuggestionBatch>
  },

  apply: async (
    input: ApplyTitleSuggestionsInput,
  ): Promise<ApplyTitleSuggestionsResponse> => {
    const response = await fetch("/api/admin/title-suggestions/apply", {
      method: "POST",
      credentials: "include",
      headers: postHeaders(),
      body: JSON.stringify(input),
    })
    if (!response.ok)
      throw await readError(response, "Failed to apply rank title suggestions")
    return response.json() as Promise<ApplyTitleSuggestionsResponse>
  },
}
